import type { resourceEventHistoryApiSpec } from "@wingspanhq/wingspan-api-types";
import { EventGroup, GROUP_WINDOW_MS } from "./types";

const sortByTimestampDescending = (
  events: resourceEventHistoryApiSpec.IResourceEvent[]
): resourceEventHistoryApiSpec.IResourceEvent[] =>
  [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

const getGroupingKey = (
  event: resourceEventHistoryApiSpec.IResourceEvent
): string => `${event.userName ?? ""}::${event.type ?? ""}`;

const isWithinGroupWindow = (
  anchorTime: number,
  candidateTime: number
): boolean => Math.abs(anchorTime - candidateTime) <= GROUP_WINDOW_MS;

const toEventGroup = (
  events: resourceEventHistoryApiSpec.IResourceEvent[]
): EventGroup => {
  const anchor = events[0];

  if (events.length === 1) {
    return { type: "single", event: anchor };
  }

  return {
    type: "grouped",
    events,
    actor: anchor.userName || "Automated",
    eventType: anchor.type || "",
    eventTitle: anchor.title,
    anchorTimestamp: new Date(anchor.timestamp)
  };
};

/**
 * Groups consecutive events by actor (userName), event type, and time window.
 * Events are grouped only when they are adjacent (no other events in-between),
 * share the same actor and type, and occur within 30 minutes of the anchor.
 */
export function groupEvents(
  events: resourceEventHistoryApiSpec.IResourceEvent[]
): EventGroup[] {
  if (events.length === 0) return [];

  const sorted = sortByTimestampDescending(events);
  const groups: EventGroup[] = [];
  let i = 0;

  while (i < sorted.length) {
    const anchor = sorted[i];

    if (!anchor.type) {
      groups.push({ type: "single", event: anchor });
      i++;
      continue;
    }

    const anchorKey = getGroupingKey(anchor);
    const anchorTime = new Date(anchor.timestamp).getTime();
    const groupedEvents = [anchor];

    let j = i + 1;
    while (j < sorted.length) {
      const candidate = sorted[j];
      if (
        getGroupingKey(candidate) === anchorKey &&
        isWithinGroupWindow(anchorTime, new Date(candidate.timestamp).getTime())
      ) {
        groupedEvents.push(candidate);
        j++;
      } else {
        break;
      }
    }

    groups.push(toEventGroup(groupedEvents));
    i = j;
  }

  return groups;
}
